import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import LoadingIndicator from 'primitives/LoadingIndicator';

import AuditsPreview from 'components/Audit/AuditsPreview';
import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';

import DeleteCompanyButton from './DeleteCompanyButton';
import UpdateCompany from './UpdateCompany';
import UpdateCompanyStatus from './UpdateCompanyStatus';

const COMPANY_QUERY = gql(`
  query getCompanyInformation($id: ID!) {
    company(id: $id) {
      id
      oldPlatformId
      name
      legalName
      countryOfIncorporation
      dateOfIncorporation
      cin
      pan
      website
      image
      description
      status
      createdAt
      updatedAt
      statistics {
        currentValue
        numberOfAssets
        numberOfDeals
        totalInvestmentAmount
        realizedValue
      }
    }
  }
`);

const COMPANY_HOLDINGS_QUERY = gql(`
  query GetAngelListIndiaCompanyHoldings($companyId: ID!) {
    angelListIndiaCompanyHoldings(companyId: $companyId) {
      companyInvestments {
        investeeName
        schemeName
        investmentDate
        numberOfUnits
        drawdown
        investment
        setupFee
        estimatedFMV
        footnote
      }
      exitedCompanyInvestments {
        investeeName
        schemeName
        investmentDate
        investment
        proceedsFromExit
        distributedAmount
        footnote
      }
    }
  }
`);

const CompanyInformation: FC = () => {
  const { companyId } = useParams<{ companyId: string }>() as {
    companyId: string;
  };

  const { loading, error, data, refetch } = useQuery(COMPANY_QUERY, {
    variables: {
      id: companyId,
    },
  });

  const {
    data: angelListIndiaCompanyHoldings,
    loading: angelListIndiaCompanyHoldingsLoading,
    error: angelListIndiaCompanyHoldingsError,
  } = useQuery(COMPANY_HOLDINGS_QUERY, {
    variables: {
      companyId,
    },
  });

  if (loading || angelListIndiaCompanyHoldingsLoading) return <LoadingIndicator />;

  if (error || angelListIndiaCompanyHoldingsError || !data || !angelListIndiaCompanyHoldings)
    return <ErrorMessage error={error || angelListIndiaCompanyHoldingsError} refetch={refetch} />;

  const company = data.company;
  const statistics = company.statistics;

  const { companyInvestments, exitedCompanyInvestments } =
    angelListIndiaCompanyHoldings.angelListIndiaCompanyHoldings;

  return (
    <div className="flex flex-col gap-y-8">
      {companyInvestments.length > 0 && (
        <DetailsPanel
          displayHorizontally
          title="Current AL India Investments"
          data={companyInvestments.map(investment => ({
            label: investment.schemeName,
            type: 'CUSTOM',
            value: investment,
            customValue: () => (
              <div className="space-y-1">
                <p>
                  <span className="font-medium">Investment Date:</span>{' '}
                  <span className="font-bold">{investment.investmentDate || 'N/A'}</span>
                </p>
                <p>
                  <span className="font-medium">Number of Units:</span>{' '}
                  <span className="font-bold">{investment.numberOfUnits}</span>
                </p>
                <p>
                  <span className="font-medium">Drawdown:</span> ₹
                  <span className="font-bold">{investment.drawdown.toLocaleString()}</span>
                </p>
                <p>
                  <span className="font-medium">Investment:</span> ₹
                  <span className="font-bold">{investment.investment.toLocaleString()}</span>
                </p>
                <p>
                  <span className="font-medium">Setup Fee:</span> ₹
                  <span className="font-bold">{investment.setupFee.toLocaleString()}</span>
                </p>
                <p>
                  <span className="font-medium">Estimated FMV:</span> ₹
                  <span className="font-bold">{investment.estimatedFMV.toLocaleString()}</span>
                </p>
                {investment.footnote && (
                  <p className="text-sm text-gray-500 mt-2">{investment.footnote}</p>
                )}
              </div>
            ),
          }))}
        />
      )}

      {exitedCompanyInvestments.length > 0 && (
        <DetailsPanel
          displayHorizontally
          title="Exited AL India Investments"
          data={exitedCompanyInvestments.map(investment => ({
            label: investment.schemeName,
            type: 'CUSTOM',
            value: investment,
            customValue: () => (
              <div className="space-y-2">
                <p>
                  <span className="font-medium">Investment Date:</span>{' '}
                  <span className="font-bold">{investment.investmentDate || 'N/A'}</span>
                </p>
                <p>
                  <span className="font-medium">Investment:</span> ₹
                  <span className="font-bold">{investment.investment.toLocaleString()}</span>
                </p>
                <p>
                  <span className="font-medium">Proceeds from Exit:</span> ₹
                  <span className="font-bold">{investment.proceedsFromExit.toLocaleString()}</span>
                </p>
                <p>
                  <span className="font-medium">Distributed Amount:</span> ₹
                  <span className="font-bold">{investment.distributedAmount.toLocaleString()}</span>
                </p>
                {investment.footnote && (
                  <p className="text-sm text-gray-500 mt-2">{investment.footnote}</p>
                )}
              </div>
            ),
          }))}
        />
      )}
      <DetailsPanel
        data={[
          {
            label: 'Old Platform (Startup) ID',
            value: company.oldPlatformId,
          },
        ]}
      />
      <UpdateCompany
        id={company.id}
        name={company.name}
        legalName={company.legalName}
        countryOfIncorporation={company.countryOfIncorporation}
        dateOfIncorporation={company.dateOfIncorporation}
        description={company.description}
        cin={company.cin}
        pan={company.pan}
        website={company.website}
        image={company.image}
      />
      <UpdateCompanyStatus id={company.id} status={company.status} />
      <DetailsPanel
        title="Statistics"
        data={[
          {
            label: 'Total Investment Amount',
            value: statistics.totalInvestmentAmount,
            type: 'CURRENCY',
          },
          { label: 'Current Value', value: statistics.currentValue, type: 'CURRENCY' },
          { label: 'Realized Value', value: statistics.realizedValue, type: 'CURRENCY' },
          { label: 'Number of Deals', value: statistics.numberOfDeals, type: 'NUMBER' },
          { label: 'Number of Assets', value: statistics.numberOfAssets, type: 'NUMBER' },
        ]}
      />
      <AuditsPreview id={company.id} type="COMPANY" />
      <DeleteCompanyButton id={company.id} />
    </div>
  );
};

export default CompanyInformation;
